import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from '../../contexts/LocationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave } = FiIcons;

const LocationForm = ({ location, onClose }) => {
  const { addLocation, updateLocation } = useLocation();
  const [formData, setFormData] = useState({
    name: location?.name || '',
    address: location?.address || '',
    phone: location?.phone || '',
    isActive: location?.isActive ?? true,
    workingHours: location?.workingHours || {
      monday: { open: '07:00', close: '19:00', isOpen: true },
      tuesday: { open: '07:00', close: '19:00', isOpen: true },
      wednesday: { open: '07:00', close: '19:00', isOpen: true },
      thursday: { open: '07:00', close: '19:00', isOpen: true },
      friday: { open: '07:00', close: '20:00', isOpen: true },
      saturday: { open: '08:00', close: '20:00', isOpen: true },
      sunday: { open: '08:00', close: '18:00', isOpen: true }
    },
    settings: location?.settings || {
      allowOnlineOrdering: true,
      estimatedPrepTime: 15,
      currency: 'USD'
    }
  });
  const [error, setError] = useState('');

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [field]: value
        }
      }
    }));
  };

  const handleSettingsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.address || !formData.phone) {
      setError('Please fill in all required fields');
      return;
    }

    const result = location 
      ? updateLocation(location.id, formData)
      : addLocation(formData);

    if (result.success) {
      onClose();
    } else {
      setError(result.error);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-warm-brown/10 p-6 flex items-center justify-between">
            <h2 className="text-2xl font-playfair text-warm-brown">
              {location ? 'Edit Location' : 'Add New Location'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} className="text-warm-brown text-xl" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
              >
                {error}
              </motion.div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-playfair text-warm-brown">Basic Information</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-warm-brown font-medium mb-2">Location Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                  />
                </div>
                <div>
                  <label className="block text-warm-brown font-medium mb-2">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-warm-brown font-medium mb-2">Address *</label>
                <textarea
                  required
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="w-4 h-4 text-warm-brown border-warm-brown/20 rounded focus:ring-warm-brown/50"
                />
                <label htmlFor="isActive" className="text-warm-brown font-medium">
                  Location is active
                </label>
              </div>
            </div>

            {/* Working Hours */}
            <div className="space-y-4">
              <h3 className="text-lg font-playfair text-warm-brown">Working Hours</h3>
              <div className="space-y-3">
                {days.map(day => (
                  <div key={day.key} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center p-3 bg-cream rounded-lg">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id={`${day.key}-open`}
                        checked={formData.workingHours[day.key].isOpen}
                        onChange={(e) => handleHoursChange(day.key, 'isOpen', e.target.checked)}
                        className="w-4 h-4 text-warm-brown border-warm-brown/20 rounded focus:ring-warm-brown/50"
                      />
                      <label htmlFor={`${day.key}-open`} className="text-warm-brown font-medium">
                        {day.label}
                      </label>
                    </div>
                    
                    {formData.workingHours[day.key].isOpen && (
                      <>
                        <div>
                          <label className="block text-warm-brown/70 text-sm mb-1">Open</label>
                          <input
                            type="time"
                            value={formData.workingHours[day.key].open}
                            onChange={(e) => handleHoursChange(day.key, 'open', e.target.value)}
                            className="w-full px-3 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                          />
                        </div>
                        <div>
                          <label className="block text-warm-brown/70 text-sm mb-1">Close</label>
                          <input
                            type="time"
                            value={formData.workingHours[day.key].close}
                            onChange={(e) => handleHoursChange(day.key, 'close', e.target.value)}
                            className="w-full px-3 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                          />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-playfair text-warm-brown">Settings</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-warm-brown font-medium mb-2">Estimated Prep Time (minutes)</label>
                  <input
                    type="number"
                    min="5"
                    max="60"
                    value={formData.settings.estimatedPrepTime}
                    onChange={(e) => handleSettingsChange('estimatedPrepTime', parseInt(e.target.value) || 15)}
                    className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                  />
                </div>
                <div>
                  <label className="block text-warm-brown font-medium mb-2">Currency</label>
                  <select
                    value={formData.settings.currency}
                    onChange={(e) => handleSettingsChange('currency', e.target.value)}
                    className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="allowOnlineOrdering"
                  checked={formData.settings.allowOnlineOrdering}
                  onChange={(e) => handleSettingsChange('allowOnlineOrdering', e.target.checked)}
                  className="w-4 h-4 text-warm-brown border-warm-brown/20 rounded focus:ring-warm-brown/50"
                />
                <label htmlFor="allowOnlineOrdering" className="text-warm-brown font-medium">
                  Allow online ordering for this location
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-warm-brown text-cream py-3 rounded-lg font-medium hover:bg-warm-brown/90 transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiSave} className="text-sm" />
                {location ? 'Update Location' : 'Add Location'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-warm-brown/20 text-warm-brown py-3 rounded-lg font-medium hover:bg-warm-brown/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LocationForm;